import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import './notification-preferences.css';

/**
 * Bildirim Tercihleri — avatar menüsünün içinde açılan bölüm.
 * Backend yalnızca kullanıcının yetkisi olan bildirim türlerini listeler
 * (herkese açık olanlar + sahip olunan yetkiler); her tür için CRM (zil/panel)
 * ve E-posta kanalı anahtarı. Değişiklik anında kaydedilir.
 * Katalog: aycrm_backend/src/config/notificationTypes.js
 */

const API = `${process.env.REACT_APP_API_URL}/notification-preferences`;

interface PrefType {
  key: string;
  label: string;
  description: string;
  channels: Array<'crm' | 'email'>;
  public: boolean;
  crm: boolean | null;
  email: boolean | null;
}
interface PrefGroup {
  group: string;
  types: PrefType[];
}

const Icon: React.FC<{ d: string; size?: number }> = ({ d, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const ICON = {
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6',
  chevron: 'M6 9l6 6 6-6',
};

const NotificationPreferencesPanel: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const [groups, setGroups] = useState<PrefGroup[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setGroups(res.data?.groups || []);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Bildirim tercihleri yüklenemedi', { autoClose: 3000 });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && groups === null) load();
  }, [open, groups, load]);

  const toggle = async (t: PrefType, channel: 'crm' | 'email') => {
    const next = !(t[channel] !== false);
    const id = `${t.key}:${channel}`;
    setBusy(id);
    // iyimser güncelleme
    setGroups((prev) => (prev ? prev.map((g) => ({ ...g, types: g.types.map((x) => (x.key === t.key ? { ...x, [channel]: next } : x)) })) : prev));
    try {
      await axios.post(API, { type_key: t.key, [channel]: next });
      setSavedKey(id);
      window.setTimeout(() => setSavedKey((s) => (s === id ? null : s)), 1400);
    } catch (err: any) {
      setGroups((prev) => (prev ? prev.map((g) => ({ ...g, types: g.types.map((x) => (x.key === t.key ? { ...x, [channel]: !next } : x)) })) : prev));
      toast.error(err?.response?.data?.message || 'Tercih kaydedilemedi', { autoClose: 3000 });
    } finally {
      setBusy(null);
    }
  };

  const total = groups ? groups.reduce((n, g) => n + g.types.length, 0) : 0;

  return (
    <div className="nprefs">
      <button type="button" className={`nprefs-head ${open ? 'open' : ''}`} onClick={onToggle} aria-expanded={open}>
        <span className="nprefs-head-icon">
          <Icon d={ICON.bell} size={15} />
        </span>
        <span className="nprefs-head-text">
          <span className="nprefs-head-title">Bildirim Tercihleri</span>
          <span className="nprefs-head-sub">{groups ? `${total} bildirim türü · CRM / E-posta` : 'Hangi bildirimi nereden alacağınızı seçin'}</span>
        </span>
        <motion.span className="nprefs-head-chevron" animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <Icon d={ICON.chevron} size={14} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="nprefs-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
          >
            <div className="nprefs-cols">
              <span />
              <span title="CRM içi bildirim (zil / panel)">
                <Icon d={ICON.bell} size={12} /> CRM
              </span>
              <span title="E-posta">
                <Icon d={ICON.mail} size={12} /> E-posta
              </span>
            </div>
            <div className="nprefs-scroll">
              {loading && groups === null && (
                <div className="nprefs-skel">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} />
                  ))}
                </div>
              )}
              {groups && groups.length === 0 && !loading && <div className="nprefs-empty">Size uygun bildirim türü bulunamadı.</div>}
              {groups?.map((g) => (
                <div className="nprefs-group" key={g.group}>
                  <div className="nprefs-group-title">{g.group}</div>
                  {g.types.map((t) => (
                    <div className={`nprefs-row ${savedKey?.startsWith(`${t.key}:`) ? 'saved' : ''}`} key={t.key}>
                      <div className="nprefs-row-text">
                        <div className="nprefs-row-label">
                          {t.label}
                          {t.public && <span className="nprefs-tag">Herkese açık</span>}
                        </div>
                        <div className="nprefs-row-desc">{t.description}</div>
                      </div>
                      {(['crm', 'email'] as const).map((ch) =>
                        t.channels.includes(ch) ? (
                          <label
                            key={ch}
                            className={`nprefs-switch ${t[ch] !== false ? 'on' : ''} ${busy === `${t.key}:${ch}` ? 'busy' : ''}`}
                            title={ch === 'crm' ? 'CRM içi bildirim' : 'E-posta'}
                          >
                            <input
                              type="checkbox"
                              checked={t[ch] !== false}
                              disabled={busy === `${t.key}:${ch}`}
                              onChange={() => toggle(t, ch)}
                              aria-label={`${t.label} · ${ch === 'crm' ? 'CRM' : 'E-posta'}`}
                            />
                            <span className="nprefs-knob" />
                          </label>
                        ) : (
                          <span key={ch} className="nprefs-na" title="Bu tür bu kanaldan gönderilmez">
                            —
                          </span>
                        ),
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="nprefs-foot">Kapattığınız kanaldan o bildirim artık gelmez; değişiklikler anında kaydedilir.</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationPreferencesPanel;
